import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import parentPagesData from '@vibesites/theme-vantage/pages.json';
import companyPagesData from '@vibesites-price-brothers/theme-vantage/pages.json';
import sitePagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { contrastRatio, parseColor } from '../utils/luminance';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageSection {
  slot: string;
}

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
  sections?: PageSection[];
}

/** Merge base → company → site pages (shallow per alias, like the deploy pipeline). */
const pageLayers = [parentPagesData, companyPagesData, sitePagesData] as Record<string, Partial<PageEntry>>[];
const pagesData = Object.fromEntries(
  Object.keys(Object.assign({}, ...pageLayers)).map((alias) => [
    alias,
    Object.assign({}, ...pageLayers.map((layer) => layer[alias] || {})) as PageEntry,
  ]),
) as Record<string, PageEntry>;

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'fair-housing',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface NavGroupItem {
  alias: string;
  /** Overrides the page's `name` only while the group is expanded; a group
      that collapses to this single page falls back to `name`. Link text
      otherwise always comes from page settings, so don't add other label
      overrides here. */
  groupedLabel?: string;
}

interface NavGroupConfig {
  label: string;
  items: NavGroupItem[];
}

/** Drawer menu structure, in order. A bare alias renders as a single link;
    a group renders as an expandable heading over its visible pages. Groups
    with one visible page collapse to a plain link, and empty groups are
    dropped. */
const NAV_STRUCTURE: Array<string | NavGroupConfig> = [
  'floor-plans',
  {
    label: 'Gallery',
    items: [{ alias: 'gallery' }, { alias: 'virtual-tour' }],
  },
  {
    label: 'Amenities & Pets',
    items: [{ alias: 'amenities' }, { alias: 'pet-policy' }],
  },
  {
    label: 'Explore',
    items: [
      { alias: 'neighborhood' },
      { alias: 'interactive-map' },
      { alias: 'sister-properties' },
    ],
  },
  'contact',
  {
    label: 'About',
    items: [{ alias: 'faq' }, { alias: 'preferred-employer-program' }],
  },
];

type NavEntry =
  | { kind: 'link'; link: NavLink }
  | { kind: 'group'; label: string; links: NavLink[] };

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface MenuGalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
}

/**
 * Returns true when the page's primary hero renders the right-edge action rail
 * (home `Hero` or interior `HeroSolidBG`). On those pages the header owns a
 * morphing fixed hamburger on desktop (lg+) that overlays the rail's top slot.
 */
function pageHasHeroRail(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return hero?.slot === 'Hero' || hero?.slot === 'HeroSolidBG';
}

/**
 * Build the drawer menu from NAV_STRUCTURE + pages.json.
 * Only pages that are visible (and not template pages) are included.
 * Visible pages missing from NAV_STRUCTURE and the exclusion list are
 * appended as top-level links, sorted by position, so a page is never
 * silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state (Centre Point being the canonical
 * example — it has neither, but earlier versions of this check let it
 * leak into the nav when only one ID was populated as a placeholder).
 * If either is empty we drop the page from the nav (the SSG also marks
 * the page hidden + noindex so it isn't crawled — see ssg-entry.ts).
 */
function buildNavEntries(
  hasVirtualTours: boolean,
  hasInteractiveLeasing: boolean,
): NavEntry[] {
  const isEligible = (alias: string): boolean => {
    const page = pagesData[alias];
    if (!page || !page.path) return false;
    if (page.visible === false) return false;
    if (page.template) return false;
    if (alias === 'virtual-tour' && !hasVirtualTours) return false;
    if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
    return true;
  };

  const placed = new Set<string>();
  const toLink = (alias: string, label?: string): NavLink => {
    placed.add(alias);
    return { label: label || pagesData[alias].name, href: pagesData[alias].path };
  };

  const entries: NavEntry[] = [];
  for (const item of NAV_STRUCTURE) {
    if (typeof item === 'string') {
      if (isEligible(item)) entries.push({ kind: 'link', link: toLink(item) });
      continue;
    }
    const visibleItems = item.items.filter(({ alias }) => isEligible(alias));
    if (visibleItems.length === 1) {
      entries.push({ kind: 'link', link: toLink(visibleItems[0].alias) });
      continue;
    }
    if (visibleItems.length > 1) {
      const links = visibleItems.map(({ alias, groupedLabel }) => toLink(alias, groupedLabel));
      entries.push({ kind: 'group', label: item.label, links });
    }
  }

  const leftovers = Object.entries(pagesData)
    .filter(([alias]) => !placed.has(alias) && !NAV_EXCLUDED_ALIASES.has(alias) && isEligible(alias))
    .sort(([, a], [, b]) => a.position - b.position);
  for (const [alias] of leftovers) {
    entries.push({ kind: 'link', link: toLink(alias) });
  }

  return entries;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const selfGuidedTourLinkDef: SlotGroupDefinition = {
  label: 'Self-Guided Tours Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
      suggestedFields: ['custom_self_guided_tour_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone Link',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    text: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    text: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function digitsOnly(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/[^+\d]/g, '');
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `tel:${digits}` : raw;
}

function toSmsUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('sms:')) return raw;
  const digits = digitsOnly(raw);
  return digits ? `sms:${digits}` : raw;
}

/** Street on line one; "City, ST ZIP" on line two. */
function renderAddressTwoLine(raw: string): React.ReactNode {
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return raw;
  const street = parts.slice(0, -2).join(', ');
  const locality = parts.slice(-2).join(', ');
  return (
    <>
      {street}
      <br />
      <span className="whitespace-nowrap">{locality}</span>
    </>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function SmartphoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

function useGalleryRotation(count: number, intervalMs = 5000) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => setActive((i) => (i + 1) % count), intervalMs);
    return () => clearInterval(id);
  }, [count, intervalMs]);

  return active;
}

function MenuGalleryCarousel({ photos }: { photos: { src: string; alt: string }[] }) {
  const active = useGalleryRotation(photos.length);

  if (photos.length === 0) return null;

  return (
    <div className="menu-gallery-carousel" aria-live="polite">
      {photos.map((photo, index) => (
        <div
          key={`${photo.src}-${index}`}
          className={`menu-gallery-carousel__slide${index === active ? ' is-active' : ''}`}
          aria-hidden={index !== active}
        >
          <VibesitesImage
            url={photo.src}
            alt={photo.alt}
            objectFit="cover"
            className="menu-gallery-carousel__image"
          />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isActive,
}: {
  link: NavLink;
  onNavigate: () => void;
  isActive?: boolean;
  /** Retained in callers for parity with desktop but intentionally
      unused — see comment below. Children render flush with their
      parents in the drawer because the visual offset reads as misaligned
      noise in a vertical list (the desktop hover dropdown already
      conveys the hierarchy there). */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={`menu-nav-link block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide text-white transition-colors${
        isActive ? ' menu-nav-link--active' : ''
      }`}
    >
      <span className="inline-flex items-center gap-2">
        {link.label}
        {link.children?.length ? (
          <ChevronDownIcon className="w-4 h-4 opacity-70" />
        ) : null}
      </span>
    </a>
  );
}

function MobileNavGroup({
  label,
  links,
  onNavigate,
  isNavActive,
}: {
  label: string;
  links: NavLink[];
  onNavigate: () => void;
  isNavActive: (href: string) => boolean;
}) {
  const containsActive = links.some((link) => isNavActive(link.href));
  const [open, setOpen] = useState(containsActive);
  const openedByHover = useRef(false);
  const panelId = `menu-group-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  // Hover-to-open applies to mouse pointers only; touch pointers fire
  // pointerenter on tap and would fight the click toggle.
  const isHoverPointer = (e: React.PointerEvent) => e.pointerType === 'mouse';

  const handlePointerEnter = (e: React.PointerEvent) => {
    if (!isHoverPointer(e) || open) return;
    openedByHover.current = true;
    setOpen(true);
  };

  const handlePointerLeave = (e: React.PointerEvent) => {
    if (!isHoverPointer(e) || !openedByHover.current) return;
    openedByHover.current = false;
    setOpen(false);
  };

  const handleToggleClick = () => {
    // Clicking a hover-opened group pins it open instead of collapsing it.
    if (openedByHover.current) {
      openedByHover.current = false;
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <div
      className="menu-nav-group"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <button
        type="button"
        onClick={handleToggleClick}
        aria-expanded={open}
        aria-controls={panelId}
        className={`menu-nav-link menu-nav-group-toggle w-full text-left block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide transition-colors${
          containsActive ? ' menu-nav-link--active' : ''
        }`}
      >
        <span className="inline-flex items-center gap-2">
          {label}
          <ChevronDownIcon
            className={`w-4 h-4 opacity-70 transition-transform duration-300${open ? ' rotate-180' : ''}`}
          />
        </span>
      </button>
      <div
        id={panelId}
        className={`menu-nav-group-panel${open ? ' is-open' : ''}`}
        aria-hidden={!open}
      >
        <div className="menu-nav-group-inner">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={onNavigate}
              tabIndex={open ? undefined : -1}
              className={`menu-nav-sublink block py-1.5 md:py-2 font-body text-sm md:text-base tracking-wide transition-colors${
                isNavActive(link.href) ? ' menu-nav-link--active' : ''
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    let ticking = false;
    const SCROLL_AT = 20;
    const UNSCROLL_AT = 8;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(prev => {
          if (!prev && y > SCROLL_AT) return true;
          if (prev && y < UNSCROLL_AT) return false;
          return prev;
        });
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  // True on pages whose hero renders the action rail (home + interior heroes).
  // A single fixed hamburger morphs between the large rail-tile size at the
  // top of the hero and a compact corner button once scrolled. Below lg the
  // rail is hidden, so the standard header hamburger is used instead
  // (tablet matches mobile).
  const heroHasRail = useMemo(
    () =>
      pageHasHeroRail(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );
  const hideBarActions = heroHasRail;

  // Desktop rail pages: one always-mounted fixed hamburger whose size and
  // vertical position interpolate from the hero rail tile to the compact
  // corner button as the page scrolls — no unmount, no entrance animation.
  useEffect(() => {
    if (!heroHasRail) return;

    let raf = 0;
    const MORPH_END = 96;
    let lastTop = -1;
    let lastHeight = -1;
    let lastWidth = -1;

    const update = () => {
      raf = 0;
      if (window.innerWidth < 1024) return;

      const hero = document.querySelector('.hero-rail-anchor') as HTMLElement | null;
      if (!hero) return;

      const bannerH = Math.round(
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--offer-banner-height')) || 0,
      );
      const heroRect = hero.getBoundingClientRect();
      const isLg = window.matchMedia('(min-width: 1024px)').matches;
      const compactH = isLg ? 96 : 88;
      const rail = hero.querySelector('.hero-rail') as HTMLElement | null;
      const cssRailWidth = parseFloat(getComputedStyle(hero).getPropertyValue('--hero-rail-width'));
      const expandedW = !Number.isNaN(cssRailWidth) && cssRailWidth > 0
        ? Math.round(cssRailWidth)
        : rail
          ? Math.round(rail.getBoundingClientRect().width)
          : isLg
            ? 220
            : 180;

      // Measure the rail spacer directly — interior heroes keep the home tile
      // size via --hero-rail-width (viewport / 5), not heroHeight / 5. Reading
      // --hero-rail-hamburger-height fails when it is declared as var(--hero-rail-width).
      const spacer = hero.querySelector('.hero-rail-hamburger-spacer') as HTMLElement | null;
      const spacerRect = spacer?.getBoundingClientRect();
      const railH = Math.max(0, heroRect.height - bannerH);
      const slotCount = parseInt(
        getComputedStyle(hero).getPropertyValue('--hero-rail-slot-count') || '5',
        10,
      );
      const tileH =
        spacerRect && spacerRect.height > 0
          ? Math.round(spacerRect.height)
          : !Number.isNaN(cssRailWidth) && cssRailWidth > 0
            ? Math.round(cssRailWidth)
            : Math.round(railH / (slotCount > 0 ? slotCount : 5));

      // Hero-relative scroll — reliable when SPA navigation preserves scrollY.
      const scrollIntoHero = Math.max(0, -heroRect.top);
      const t = Math.min(1, Math.max(0, scrollIntoHero / MORPH_END));

      const railTop = spacerRect?.top ?? heroRect.top + bannerH;
      const compactTop = bannerH;
      const top = Math.round(railTop + (compactTop - railTop) * t);
      // +1 seals the subpixel seam against the first rail tile while expanded.
      const height = Math.round(tileH + (compactH - tileH) * t) + 1;
      // Width stays at the square rail tile width — only height/top shrink on scroll.
      const width = expandedW;

      if (top === lastTop && height === lastHeight && width === lastWidth) return;
      lastTop = top;
      lastHeight = height;
      lastWidth = width;

      const root = document.documentElement.style;
      root.setProperty('--rail-hamburger-top', `${top}px`);
      root.setProperty('--rail-hamburger-height', `${height}px`);
      root.setProperty('--rail-hamburger-width', `${width}px`);
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    schedule();
    const mountRetry = requestAnimationFrame(() => {
      requestAnimationFrame(schedule);
    });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    const hero = document.querySelector('.hero-rail-anchor');
    const ro = hero ? new ResizeObserver(schedule) : null;
    if (hero) {
      ro!.observe(hero);
      const spacer = hero.querySelector('.hero-rail-hamburger-spacer');
      if (spacer) ro!.observe(spacer);
    }

    const banner = document.querySelector('.sob');
    const bannerRo = banner ? new ResizeObserver(schedule) : null;
    if (banner) bannerRo!.observe(banner);

    return () => {
      cancelAnimationFrame(mountRetry);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro?.disconnect();
      bannerRo?.disconnect();
      document.documentElement.style.removeProperty('--rail-hamburger-top');
      document.documentElement.style.removeProperty('--rail-hamburger-height');
      document.documentElement.style.removeProperty('--rail-hamburger-width');
    };
  }, [heroHasRail, pageCtx?.alias]);

  const tokens = useTokens();

  // CTA accent color for the SCROLLED (secondary-background) nav state. The
  // outline-style `Schedule a Tour` button paints its border / label in this
  // color. Prefer primary against the secondary header; when primary is too
  // close to secondary (contrast < WCAG AA 4.5:1), fall back to
  // text-on-secondary so the CTA stays legible.
  const [navCtaAccent, navCtaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const secondary = tokens?.color?.secondary;
    if (!primary || !secondary) {
      return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    }
    return contrastRatio(primary, secondary) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text-on-secondary)', 'var(--vibe-color-secondary)'] as const;
  }, [tokens]);

  // Active-page color in the dark drawer: the secondary color, tinted toward
  // white only as far as needed to reach WCAG AAA (7:1) on the drawer surface,
  // so it stands out against the near-white inactive links.
  const menuActiveColor = useMemo(() => {
    const rgb = parseColor(tokens?.color?.secondary);
    if (!rgb) return 'var(--vibe-color-secondary)';
    const drawerSurface = '#0b0c10';
    for (let step = 0; step <= 10; step += 1) {
      const t = step / 10;
      const mix = {
        r: Math.round(rgb.r + (255 - rgb.r) * t),
        g: Math.round(rgb.g + (255 - rgb.g) * t),
        b: Math.round(rgb.b + (255 - rgb.b) * t),
      };
      if (contrastRatio(mix, drawerSurface) >= 7) {
        return `rgb(${mix.r}, ${mix.g}, ${mix.b})`;
      }
    }
    return '#ffffff';
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const navEntries = useMemo(
    () => buildNavEntries(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  const currentPath = pageCtx?.path
    || (typeof window !== 'undefined' ? window.location.pathname : '/');

  const isNavActive = useCallback((href: string) => {
    const normalized = currentPath.endsWith('/') && currentPath !== '/'
      ? currentPath.slice(0, -1)
      : currentPath;
    const target = href.endsWith('/') && href !== '/'
      ? href.slice(0, -1)
      : href;
    if (target === '/') return normalized === '/';
    return normalized === target || normalized.startsWith(`${target}/`);
  }, [currentPath]);

  // Property featured image — used as the dimmed backdrop behind the
  // slide-in nav drawer (an image sits behind a semi-transparent black wash).
  const menuBgImage = usePropertyField('property_featured_image');
  const propertyAddressUrl = usePropertyAddressUrl();
  const callTrackingPhone = usePropertyField('property_call_tracking_phone') as string | null | undefined;
  const propertyPhone = usePropertyField('property_phone') as string | null | undefined;
  const defaultTelUrl = toTelUrl(callTrackingPhone) || toTelUrl(propertyPhone);
  const defaultSmsUrl = toSmsUrl(callTrackingPhone) || toSmsUrl(propertyPhone);

  const menuGallery = useCollectionBinding<MenuGalleryPhoto>('header.menu_gallery', {
    collection: 'photo_gallery',
    defaultLimit: 5,
  });
  const menuGalleryPhotos = useMemo(() => {
    // Skip non-photo gallery entries by title, mirroring PhotoGallery — these
    // categories ("Logo", "Other", "Floorplan") aren't real photos and read as
    // noise in the auto-rotating drawer carousel.
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return (menuGallery || [])
      .filter((photo) => !excludedTitles.has((photo.title || '').toLowerCase().trim()))
      .slice(0, 5)
      .map((photo) => ({
        src: photo.image ?? photo.original_url ?? '',
        alt: photo.alt_text || '',
      }))
      .filter((photo) => photo.src);
  }, [menuGallery]);

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const primaryLogo = usePropertyField('property_primary_logo');
  // Transparent header: white mono. Scrolled (secondary bar): primary-color
  // logo — the brand primary mark on the secondary surface.
  const headerLogoWhite = monoWhiteLogo || logoRaw.image;
  const headerLogoBlack = primaryLogo || monoBlackLogo || logoRaw.image;
  const headerLogoHasSwap = !!(headerLogoWhite && headerLogoBlack && headerLogoWhite !== headerLogoBlack);

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const selfGuidedTourLink = useSlotGroup('header.self_guided_tour_link', selfGuidedTourLinkDef);
  const selfGuidedTourUrl = (selfGuidedTourLink.url || '').trim();
  const hasSelfGuidedTour = Boolean(selfGuidedTourUrl);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const phoneLink = useSlotGroup('header.phone_link', phoneLinkDef);
  const textUsLink = useSlotGroup('header.text_us_link', textUsLinkDef);
  const facebookLink = useSlotGroup('header.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('header.instagram_link', instagramLinkDef);

  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const phoneHref = toTelUrl(phoneLink.url) || defaultTelUrl;
  const textUsHref = toSmsUrl(textUsLink.url) || defaultSmsUrl;
  const phoneDisplay = formatPhone(phoneLink.text) || formatPhone(callTrackingPhone) || formatPhone(propertyPhone) || '(555) 555-5555';
  const menuLogoImage = monoWhiteLogo || logoRaw.image;
  const menuLogo = { ...logoRaw, image: menuLogoImage };

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        className={`fixed left-0 right-0 w-full ${menuOpen ? 'z-[70]' : 'z-50'}${scrolled ? ' header-scrolled' : ' bg-transparent header-transparent'}${heroHasRail ? ' header-rail-page' : ''}`}
        style={{
          top: 0,
          // Scrolled background is set in CSS (.header-scrolled) rather than
          // inline so a media query can drop it on desktop rail pages, where the
          // pinned rail hamburger replaces the header bar entirely.
          ['--vantage-nav-cta-accent' as string]: navCtaAccent,
          ['--vantage-nav-cta-on-accent' as string]: navCtaOnAccent,
          ['--vantage-menu-active' as string]: menuActiveColor,
        }}
      >
        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)' }}
        >
          <div className="header-bar-inner">
            <div className="header-bar-row flex items-center">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {headerLogoWhite || headerLogoBlack ? (
                    headerLogoHasSwap ? (
                      <span className="header-bar-logo-stack inline-flex items-center">
                        <VibesitesImage
                          url={headerLogoWhite}
                          alt={logoRaw.alt || 'Community Logo'}
                          className="header-bar-logo header-bar-logo--light"
                        />
                        <VibesitesImage
                          url={headerLogoBlack}
                          alt={logoRaw.alt || 'Community Logo'}
                          className="header-bar-logo header-bar-logo--dark"
                        />
                      </span>
                    ) : (
                      <VibesitesImage
                        url={(headerLogoWhite || headerLogoBlack) || undefined}
                        alt={logoRaw.alt || 'Community Logo'}
                        className="header-bar-logo"
                      />
                    )
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Vantage
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop navigation has been removed in favor of a single
                  hamburger menu at every breakpoint. All nav links and utility
                  actions (phone, Apply Now, Resident Access, address) now live
                  exclusively in the drawer the hamburger opens. */}

              {/* Right group — CTA + hamburger, shown at all breakpoints */}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
                {/* Schedule Tour + optional Self-Guided Tours CTAs — desktop
                    header bar only. Hidden below lg (tablet/mobile use the
                    drawer) and on rail-page desktop where the hero rail owns
                    these actions. */}
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className={`nav-cta-btn group/cta relative ${hideBarActions ? 'hidden' : 'hidden lg:inline-flex'} items-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 text-[10px] lg:text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all duration-300`}
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                  </a>
                </EditableSlotGroup>
                {hasSelfGuidedTour ? (
                  <EditableSlotGroup groupPath="header.self_guided_tour_link" definition={selfGuidedTourLinkDef}>
                    <a
                      href={selfGuidedTourUrl}
                      target={selfGuidedTourLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className={`nav-cta-btn nav-cta-btn--secondary group/cta relative ${hideBarActions ? 'hidden' : 'hidden lg:inline-flex'} items-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 text-[10px] lg:text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all duration-300`}
                    >
                      <span className="relative z-10">
                        {selfGuidedTourLink.label || 'Self-Guided Tours'}
                      </span>
                    </a>
                  </EditableSlotGroup>
                ) : (
                  <EditableSlotGroup
                    groupPath="header.self_guided_tour_link"
                    definition={selfGuidedTourLinkDef}
                    className="hidden"
                    style={{ display: 'none' }}
                  >
                    <span />
                  </EditableSlotGroup>
                )}

                {/* Hamburger — opens the full-screen drawer. Hidden on lg+ for
                    rail pages where the morphing corner hamburger is used. */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className={`${hideBarActions ? 'lg:hidden ' : ''}relative w-10 h-10 flex items-center justify-center`}
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop rail-page hamburger — always mounted, morphs between the
            large hero-rail tile and the compact corner button on scroll. */}
        {heroHasRail && (
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="header-rail-hamburger hidden lg:flex"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="header-rail-bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        )}

        {/* ============================================================== */}
        {/* Menu overlay — full-width on tablet/desktop, sheet on mobile   */}
        {/* ============================================================== */}
        <div
          aria-hidden={!menuOpen}
          className={`menu-backdrop fixed inset-0 z-[60] bg-black/40 transition-opacity duration-300 md:hidden ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={closeMobileMenu}
        />

        <div
          aria-hidden={!menuOpen}
          className={`mobile-menu-panel fixed right-0 top-0 bottom-0 w-[calc(100%-3rem)] md:w-full overflow-hidden transition-transform duration-300 ease-out z-[61] flex flex-col ${
            menuOpen ? 'translate-x-0 mobile-menu-open' : 'translate-x-full pointer-events-none'
          }`}
        >
          {/* Backdrop image — sits behind the black wash. */}
          <div className="menu-bg" aria-hidden="true">
            {menuBgImage ? (
              <VibesitesImage
                url={menuBgImage}
                alt=""
                objectFit="cover"
                className="w-full h-full object-cover"
              />
            ) : null}
          </div>
          {/* Black semi-transparent wash over the image. */}
          <div className="menu-overlay" aria-hidden="true" />

          <div className="menu-panel-header relative z-20 flex items-center justify-between px-6 md:px-10 lg:px-14 pt-6 md:pt-8">
            <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
              <a href="/" onClick={closeMobileMenu} className="flex-shrink-0">
                {menuLogo.image ? (
                  <VibesitesImage
                    url={menuLogo.image}
                    alt={logoRaw.alt || 'Community Logo'}
                    className="h-10 md:h-12 w-auto object-contain"
                  />
                ) : (
                  <div className="font-heading text-xl md:text-2xl font-bold text-white">
                    The Vantage
                  </div>
                )}
              </a>
            </EditableSlotGroup>

            <button
              type="button"
              onClick={closeMobileMenu}
              className="menu-close-btn"
              aria-label="Close menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="menu-scroll relative z-10 flex-1 overflow-y-auto pb-10 md:pb-12">
            <div className="menu-layout flex flex-col md:flex-row md:items-stretch md:min-h-[calc(100%-5.5rem)] px-6 md:px-10 lg:px-14 pt-6 md:pt-10 gap-10 md:gap-0">
              {/* Left column — navigation */}
              <nav role="navigation" aria-label="Main navigation" className="menu-nav md:flex-1 md:max-w-[42%] lg:max-w-[38%]">
                <MobileNavLink
                  link={{ label: 'Home', href: '/' }}
                  onNavigate={closeMobileMenu}
                  isActive={isNavActive('/')}
                />
                {navEntries.map((entry) =>
                  entry.kind === 'group' ? (
                    <MobileNavGroup
                      key={`group-${entry.label}`}
                      label={entry.label}
                      links={entry.links}
                      onNavigate={closeMobileMenu}
                      isNavActive={isNavActive}
                    />
                  ) : (
                    <MobileNavLink
                      key={entry.link.href}
                      link={entry.link}
                      onNavigate={closeMobileMenu}
                      isActive={isNavActive(entry.link.href)}
                    />
                  ),
                )}
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className={`menu-nav-link block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide text-white transition-colors${
                    isNavActive(scheduleTourLink.url || '/schedule-a-tour') ? ' menu-nav-link--active' : ''
                  }`}
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
                {hasSelfGuidedTour ? (
                  <a
                    href={selfGuidedTourUrl}
                    target={selfGuidedTourLink._target || '_blank'}
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                    className="menu-nav-link block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide text-white transition-colors"
                  >
                    {selfGuidedTourLink.label || 'Self-Guided Tours'}
                  </a>
                ) : null}
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className={`menu-nav-link block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide text-white transition-colors${
                      isNavActive(applyNowLink.url || '/apply-now') ? ' menu-nav-link--active' : ''
                    }`}
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className={`menu-nav-link block py-2.5 md:py-3 font-body text-base md:text-lg font-medium tracking-wide text-white transition-colors${
                      isNavActive(residentAccessLink.url || '/resident-access') ? ' menu-nav-link--active' : ''
                    }`}
                  >
                    {residentAccessLink.label || 'Resident Login'}
                  </a>
                </EditableSlotGroup>
              </nav>

              {/* Divider */}
              <div className="menu-divider hidden md:block" aria-hidden="true" />

              {/* Right column — contact + gallery */}
              <div className="menu-contact md:flex-1 md:pl-12 lg:pl-16 md:pt-2">
                <div className="space-y-5 md:space-y-6">
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressHref}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="menu-contact-row group"
                    >
                      <span className="menu-contact-icon-badge flex-shrink-0" aria-hidden="true">
                        <MapPinIcon />
                      </span>
                      <span className="menu-contact-text vantage-nav-darken-hover">
                        {renderAddressTwoLine(
                          addressLink.text || '123 Main Street, Atlanta, GA 30313',
                        )}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.phone_link" definition={phoneLinkDef}>
                    <a
                      href={phoneHref || '#'}
                      target={phoneLink._target || '_self'}
                      className="menu-contact-row dniphone dniphonehref group"
                    >
                      <span className="menu-contact-icon-badge flex-shrink-0" aria-hidden="true">
                        <PhoneIcon />
                      </span>
                      <span className="menu-contact-text vantage-nav-darken-hover">
                        {phoneDisplay}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.text_us_link" definition={textUsLinkDef}>
                    <a
                      href={textUsHref || '#'}
                      target={textUsLink._target || '_self'}
                      className="menu-contact-row group"
                    >
                      <span className="menu-contact-icon-badge flex-shrink-0" aria-hidden="true">
                        <SmartphoneIcon />
                      </span>
                      <span className="menu-contact-text vantage-nav-darken-hover">
                        {textUsLink.text || 'Text Us!'}
                      </span>
                    </a>
                  </EditableSlotGroup>

                  {(facebookLink.url || instagramLink.url) ? (
                    <div className="menu-social flex items-center gap-4 pt-1">
                      {facebookLink.url ? (
                        <EditableSlotGroup groupPath="header.facebook_link" definition={facebookLinkDef}>
                          <a
                            href={facebookLink.url}
                            target={facebookLink._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={facebookLink.text || 'Facebook'}
                            className="menu-social-link"
                          >
                            <FontAwesomeIcon icon={faFacebook} className="w-5 h-5" />
                          </a>
                        </EditableSlotGroup>
                      ) : null}
                      {instagramLink.url ? (
                        <EditableSlotGroup groupPath="header.instagram_link" definition={instagramLinkDef}>
                          <a
                            href={instagramLink.url}
                            target={instagramLink._target || '_blank'}
                            rel="noopener noreferrer"
                            aria-label={instagramLink.text || 'Instagram'}
                            className="menu-social-link"
                          >
                            <FontAwesomeIcon icon={faInstagram} className="w-5 h-5" />
                          </a>
                        </EditableSlotGroup>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <MenuGalleryCarousel photos={menuGalleryPhotos} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          border-bottom: 1px solid transparent;
        }
        /* Reference hamburger-header spacing: ~22px/40px inset on mobile,
           25px top / 20px bottom / 50px left on desktop. */
        .header-bar-inner {
          padding-top: 1.375rem;
          padding-bottom: 1.375rem;
          padding-left: 2.5rem;
          padding-right: 1rem;
        }
        @media (min-width: 768px) {
          .header-bar-inner {
            padding-top: 1.5625rem;
            padding-bottom: 1.25rem;
            padding-left: 3.125rem;
            padding-right: 2rem;
          }
        }
        /* Reference hamburger-header logo proportions; modest bump on lg+. */
        .header-bar-logo {
          width: auto;
          height: auto;
          max-width: 11.625rem;
          max-height: 3.25rem;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .header-bar-logo {
            max-height: 4.375rem;
          }
        }
        @media (min-width: 1024px) {
          .header-bar-logo {
            max-width: 13.75rem;
            max-height: 5.25rem;
          }
        }
        /* Stack sizes from the in-flow light logo; the absolute dark logo
           fills that same box so white/black assets can't diverge in size. */
        .header-bar-logo-stack {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .header-bar-logo--light,
        .header-bar-logo--dark {
          transition: opacity 0.3s ease;
        }
        .header-bar-logo--dark {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          max-width: none;
          max-height: none;
          object-fit: contain;
          opacity: 0;
          pointer-events: none;
        }
        .header-scrolled .header-bar-logo--light {
          opacity: 0;
        }
        .header-scrolled .header-bar-logo--dark {
          opacity: 1;
        }
        .header-scrolled .header-bar {
          border-bottom-color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 18%, transparent);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.08),
            0 4px 12px rgba(0,0,0,0.06);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Solid background for the scrolled header. Moved out of the element's
           inline style so the rail-page rule below can drop it per breakpoint. */
        .header-scrolled {
          background-color: var(--vibe-color-secondary);
        }

        /* Rail pages (the home page) on desktop: once scrolled, remove the
           white logo bar entirely — no solid background, no logo row. The
           morphing rail hamburger stays fixed in the corner as the nav access.
           We keep the <header> mounted (it still owns the drawer + menu state)
           but make it transparent and click-through, re-enabling pointer events
           on the drawer and the rail hamburger. Below lg the rail is hidden,
           so the bar stays as the only nav — hence the lg breakpoint guard. */
        @media (min-width: 1024px) {
          .header-rail-page .header-bar {
            opacity: 1;
            visibility: visible;
            transition: opacity 0.4s ease, visibility 0s linear 0s;
          }
          .header-rail-page.header-scrolled {
            background-color: transparent;
            pointer-events: none;
          }
          .header-rail-page.header-scrolled .header-bar {
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            border-bottom-color: transparent;
            box-shadow: none;
            transition: opacity 0.4s ease, visibility 0s linear 0.4s;
          }
          .header-rail-page.header-scrolled .header-rail-hamburger {
            pointer-events: auto;
          }
          .header-rail-page.header-scrolled .mobile-menu-panel {
            pointer-events: auto;
          }
        }

        /* Click-through for the transparent header. On the home page the hero's
           action rail sits beneath the top-right of the transparent header; the
           morphing rail hamburger and the bar's own controls need to stay
           clickable. We disable pointer events on the whole transparent header,
           then re-enable them on interactive surfaces: the bar's links/buttons,
           the rail hamburger, and the entire drawer panel (which captures only
           while open — when closed it's visibility:hidden and inert regardless).
           In the scrolled / solid state this rule doesn't apply, so the bar
           captures clicks normally. pointer-events is an inherited property, so
           the panel re-enable cascades to all of its contents.

           The mobile .menu-backdrop is intentionally NOT re-enabled here: it's a
           full-screen inset-0 z-[60] element sitting above the bar, so blanket-
           enabling pointer-events would let the (invisible) closed backdrop
           swallow taps on the hamburger below it in the transparent state. Its
           open-state pointer-events are toggled directly on the element instead
           (pointer-events-auto when open, pointer-events-none when closed). */
        .header-transparent {
          pointer-events: none;
        }
        .header-transparent .header-bar a,
        .header-transparent .header-bar button,
        .header-transparent .header-rail-hamburger,
        .header-transparent .mobile-menu-panel {
          pointer-events: auto;
        }

        /* ================================================================ */
        /* Morphing rail hamburger (desktop home)                          */
        /* ================================================================ */
        .header-rail-hamburger {
          position: fixed;
          right: 0;
          z-index: 55;
          top: var(--rail-hamburger-top, var(--offer-banner-height, 0px));
          width: var(--rail-hamburger-width, 168px);
          min-width: var(--rail-hamburger-width, 168px);
          max-width: var(--rail-hamburger-width, 168px);
          height: var(--rail-hamburger-height, 88px);
          min-height: var(--rail-hamburger-height, 88px);
          max-height: var(--rail-hamburger-height, 88px);
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
          color: var(--vibe-color-text-on-primary, #fff);
          background: color-mix(in srgb, var(--vibe-color-primary) 82%, transparent);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          transition: background 0.3s ease;
        }
        .header-rail-hamburger:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 95%, transparent);
        }
        .header-rail-hamburger:focus-visible {
          outline: 2px solid var(--vibe-color-text-on-primary, #fff);
          outline-offset: -3px;
        }
        .header-rail-bars {
          position: relative;
          width: 26px;
          height: 16px;
        }
        .header-rail-bars span {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          border-radius: 2px;
          background: currentColor;
          transition: transform 0.3s ease, opacity 0.2s ease, top 0.3s ease;
        }
        .header-rail-bars span:nth-child(1) { top: 0; }
        .header-rail-bars span:nth-child(2) { top: 7px; }
        .header-rail-bars span:nth-child(3) { top: 14px; }
        .header-rail-hamburger[aria-expanded="true"] .header-rail-bars span:nth-child(1) {
          top: 7px;
          transform: rotate(45deg);
        }
        .header-rail-hamburger[aria-expanded="true"] .header-rail-bars span:nth-child(2) {
          opacity: 0;
        }
        .header-rail-hamburger[aria-expanded="true"] .header-rail-bars span:nth-child(3) {
          top: 7px;
          transform: rotate(-45deg);
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--vantage-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--vantage-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .vantage-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Transparent header: utility links → white */
        .header-transparent .header-util-link {
          color: rgba(255,255,255,0.85);
        }
        .header-transparent .header-util-link:hover {
          color: #fff;
        }
        .header-transparent .header-util-divider {
          background: rgba(255,255,255,0.3);
        }

        /* Interior pages: utility links + divider use the picked on-color
           token instead of hardcoded white. We dim the base state via
           color-mix() to match the ~85% / 30% feel of the photographic
           variant above. */
        .header-transparent.header-over-solid-hero .header-util-link {
          color: color-mix(in srgb, var(--vantage-nav-over-hero-text, #fff) 85%, transparent);
        }
        .header-transparent.header-over-solid-hero .header-util-link:hover {
          color: var(--vantage-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .header-util-divider {
          background: color-mix(in srgb, var(--vantage-nav-over-hero-text, #fff) 30%, transparent);
        }

        /* Off-canvas drawer: full-width on tablet/desktop; on mobile the panel
           leaves a left strip so visitors can tap outside to dismiss. */
        .mobile-menu-panel {
          background-color: #0b0c10;
        }
        .mobile-menu-panel.mobile-menu-open {
          box-shadow: -16px 0 50px rgba(0, 0, 0, 0.4);
        }
        @media (min-width: 768px) {
          .mobile-menu-panel.mobile-menu-open {
            box-shadow: none;
          }
        }
        .menu-backdrop {
          cursor: pointer;
        }
        .menu-panel-header {
          flex-shrink: 0;
        }
        .menu-layout {
          width: 100%;
        }
        .menu-divider {
          width: 1px;
          align-self: stretch;
          background: rgba(255, 255, 255, 0.35);
          margin: 0 2.5rem;
        }
        .menu-nav-link {
          color: rgba(255, 255, 255, 0.95);
        }
        .menu-nav-link:hover {
          color: #fff;
        }
        .menu-nav-link--active {
          color: var(--vibe-color-secondary, #7ec8e3);
        }
        .mobile-menu-panel .menu-nav-group-toggle {
          background: transparent;
          border: none;
          cursor: pointer;
        }
        .mobile-menu-panel a.menu-nav-link--active,
        .mobile-menu-panel a.menu-nav-link--active:hover,
        .mobile-menu-panel .menu-nav-group-toggle.menu-nav-link--active,
        .mobile-menu-panel .menu-nav-sublink.menu-nav-link--active,
        .mobile-menu-panel .menu-nav-sublink.menu-nav-link--active:hover {
          color: var(--vantage-menu-active, var(--vibe-color-secondary));
        }
        .menu-nav-group-panel {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.3s ease, visibility 0s linear 0.3s;
          visibility: hidden;
        }
        .menu-nav-group-panel.is-open {
          grid-template-rows: 1fr;
          transition: grid-template-rows 0.3s ease, visibility 0s linear 0s;
          visibility: visible;
        }
        .menu-nav-group-inner {
          overflow: hidden;
          margin-left: 0.25rem;
          padding-left: 1rem;
          border-left: 1px solid rgba(255, 255, 255, 0.25);
        }
        .menu-nav-group-panel.is-open .menu-nav-group-inner {
          padding-bottom: 0.5rem;
        }
        .mobile-menu-panel .menu-nav-sublink {
          color: rgba(255, 255, 255, 0.75);
        }
        .mobile-menu-panel .menu-nav-sublink:hover {
          color: #fff;
        }
        .menu-contact-row {
          display: flex;
          align-items: flex-start;
          gap: 0.85rem;
          color: rgba(255, 255, 255, 0.95);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        .menu-contact-row:hover {
          color: #fff;
        }
        .menu-contact-icon-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          flex-shrink: 0;
          color: #fff;
        }
        .menu-contact-icon-badge svg {
          width: 1.125rem;
          height: 1.125rem;
        }
        .menu-contact-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          line-height: 1.45;
        }
        @media (min-width: 768px) {
          .menu-contact-text {
            font-size: 1.125rem;
          }
        }
        .menu-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          color: var(--vibe-color-primary);
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .menu-social-link:hover {
          color: #fff;
          transform: translateY(-1px);
        }
        .menu-gallery-carousel {
          position: relative;
          width: 100%;
          max-width: 28rem;
          aspect-ratio: 4 / 3;
          margin-top: 2rem;
          overflow: hidden;
          border-radius: 2px;
        }
        .menu-gallery-carousel__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.8s ease;
        }
        .menu-gallery-carousel__slide.is-active {
          opacity: 1;
        }
        .menu-gallery-carousel__image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        /* Backdrop image + black semi-transparent wash. The image fills the
           panel and the wash darkens it so the light content reads cleanly. */
        .mobile-menu-panel .menu-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .mobile-menu-panel .menu-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: rgba(0, 0, 0, 0.82);
        }
        /* Close button — top-right of the panel header. */
        .mobile-menu-panel .menu-close-btn {
          position: static;
          width: 2.75rem;
          height: 2.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.85);
          background: transparent;
          border: none;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel .menu-close-btn:hover {
          color: #fff;
          transform: rotate(90deg);
        }
        .mobile-menu-panel .menu-close-btn svg {
          width: 1.75rem;
          height: 1.75rem;
        }

        /* Dark drawer: content renders in light text over the dimmed image. */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: rgba(255, 255, 255, 0.92);
        }
        .mobile-menu-panel a:hover {
          color: #fff;
        }
        /* Row dividers on the dark surface — nav links no longer use borders. */
        .mobile-menu-panel nav a {
          border-bottom-color: transparent;
        }

        /* Nav links: animated underline on hover (used by Header
           DesktopNavLink, "More" dropdown items, MobileNavLink, and the
           Navigation sub-bar). All of these contexts render against a
           white surface, so the affordance is an underline rather than a
           color shift — the underline is drawn via text-decoration so it
           rides the text baseline and works at any link padding. The
           transparent-header (over-hero) brightening hover above keeps
           its own color shift via earlier !important rules; the
           underline simply layers on top with currentColor. */
        .vantage-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .vantage-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Address / phone rows in the mobile drawer wear the
           vantage-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .vantage-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .vantage-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Outline pills inside the drawer flip to text-on-primary on hover. */
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Scrolled (secondary) header: hamburger + site-name use on-secondary */
        .header-scrolled span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--vibe-color-text-on-secondary) !important;
        }
        .header-scrolled .font-heading {
          color: var(--vibe-color-primary) !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--vantage-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--vantage-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          text-decoration: none;
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        /* White-background hover matches the nav links' darken-25% rule.
           The .header-transparent and .header-over-solid-hero overrides
           below carry higher specificity so they still win when the
           header sits on top of a hero. */
        .header-util-link:hover {
          color: color-mix(in srgb, var(--vibe-color-primary), black 25%);
        }
        .header-util-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: var(--vibe-color-border);
          flex-shrink: 0;
        }

        /* ================================================================ */
        /* CTA Button — transparent / outline style                        */
        /* ----------------------------------------------------------------- */
        /* Border + label use --vantage-nav-cta-accent (luminance-picked).
           Against the secondary scrolled header that resolves to
           --vibe-color-primary when primary has WCAG AA contrast, otherwise
           --vibe-color-text-on-secondary. The hover fill follows the same
           accent so the swap is consistent. */
        .nav-cta-btn {
          background: transparent;
          border: 1.5px solid var(--vantage-nav-cta-accent, var(--vibe-color-secondary));
          border-radius: 2px;
          color: var(--vantage-nav-cta-accent, var(--vibe-color-secondary));
          box-shadow: none;
        }
        .nav-cta-btn--secondary {
          border-style: dashed;
          opacity: 0.92;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vantage-nav-cta-accent, var(--vibe-color-secondary));
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vantage-nav-cta-accent, var(--vibe-color-secondary)) 25%, transparent);
          transform: translateY(-1px);
          color: var(--vantage-nav-cta-on-accent, var(--vibe-color-text-on-secondary));
        }
        .nav-cta-btn:hover::before {
          opacity: 1;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* When header is transparent (over hero) — white border & text */
        .header-transparent .nav-cta-btn {
          border-color: rgba(255,255,255,0.75);
          color: #fff;
        }
        .header-transparent .nav-cta-btn--secondary {
          border-color: rgba(255,255,255,0.55);
        }
        .header-transparent .nav-cta-btn::before {
          background: rgba(255,255,255,0.18);
        }
        .header-transparent .nav-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          box-shadow: none;
        }

        /* Interior pages: CTA border / text follow the luminance-picked
           on-color token. The translucent fill that slides in on hover uses
           color-mix() so it stays in palette when the picked color is dark. */
        .header-transparent.header-over-solid-hero .nav-cta-btn {
          border-color: color-mix(in srgb, var(--vantage-nav-over-hero-text, #fff) 75%, transparent);
          color: var(--vantage-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn::before {
          background: color-mix(in srgb, var(--vantage-nav-over-hero-text, #fff) 18%, transparent);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn:hover {
          border-color: var(--vantage-nav-over-hero-text, #fff);
          color: var(--vantage-nav-over-hero-text, #fff);
          box-shadow: none;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {},
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_white_logo', suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.self_guided_tour_link': {
      label: 'Self-Guided Tours Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'custom_self_guided_tour_url',
          suggestedFields: ['custom_self_guided_tour_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        text: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.phone_link': {
      label: 'Phone Link',
      fields: {
        text: {
          label: 'Phone Text',
          type: 'text',
          defaultField: 'property_call_tracking_phone',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.text_us_link': {
      label: 'Text Us Link',
      fields: {
        text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_call_tracking_phone',
          suggestedFields: ['property_call_tracking_phone', 'property_phone'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.facebook_link': {
      label: 'Facebook',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_facebook_url',
          suggestedFields: ['property_facebook_url'],
        },
        text: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.instagram_link': {
      label: 'Instagram',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_instagram_url',
          suggestedFields: ['property_instagram_url'],
        },
        text: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.menu_gallery': {
      label: 'Menu Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 5,
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
